import { useCallback, useEffect, useState } from 'react';

/**
 * Recents + pinned store for the admin account-finder (⌘K palette).
 *
 * LocalStorage-backed, per-browser. No DB migration — v1 is intentionally
 * client-only. Each entry carries enough to render the empty-state surface
 * (Pinned + Recents) with zero network calls.
 *
 * Keys:
 *   - brix_admin_recent_accounts  (cap 8, most-recent-first, deduped)
 *   - brix_admin_pinned_accounts  (no cap; ordered by pin time, newest first)
 */

export interface AdminAccountRef {
  /** orders.customers.id (uuid) */
  id: string;
  /** orders.customers.name */
  name: string;
  /** orders.customers.qbo_customer_id (account #), as a display string */
  number: string;
}

const RECENTS_KEY = 'brix_admin_recent_accounts';
const PINNED_KEY = 'brix_admin_pinned_accounts';
const RECENTS_CAP = 8;

/** Other tabs/components broadcast changes through this event name. */
const CHANGE_EVENT = 'brix-admin-accounts-change';

function readList(key: string): AdminAccountRef[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Defensive: keep only well-formed entries.
    return parsed.filter(
      (e): e is AdminAccountRef =>
        e && typeof e.id === 'string' && typeof e.name === 'string' && typeof e.number === 'string',
    );
  } catch {
    return [];
  }
}

function writeList(key: string, list: AdminAccountRef[]) {
  try {
    localStorage.setItem(key, JSON.stringify(list));
    // Notify other hook instances in this tab + (via storage) other tabs.
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  } catch {
    /* localStorage full / unavailable — non-fatal, recents are best-effort */
  }
}

export function useAdminAccounts() {
  const [recents, setRecents] = useState<AdminAccountRef[]>(() => readList(RECENTS_KEY));
  const [pinned, setPinned] = useState<AdminAccountRef[]>(() => readList(PINNED_KEY));

  // Stay in sync across hook instances (same tab) + other tabs (storage event).
  useEffect(() => {
    const sync = () => {
      setRecents(readList(RECENTS_KEY));
      setPinned(readList(PINNED_KEY));
    };
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const addRecent = useCallback((acct: AdminAccountRef) => {
    const next = [acct, ...readList(RECENTS_KEY).filter((e) => e.id !== acct.id)].slice(
      0,
      RECENTS_CAP,
    );
    writeList(RECENTS_KEY, next);
    setRecents(next);
  }, []);

  const isPinned = useCallback(
    (id: string) => pinned.some((e) => e.id === id),
    [pinned],
  );

  const togglePin = useCallback((acct: AdminAccountRef) => {
    const current = readList(PINNED_KEY);
    const exists = current.some((e) => e.id === acct.id);
    const next = exists
      ? current.filter((e) => e.id !== acct.id)
      : [acct, ...current];
    writeList(PINNED_KEY, next);
    setPinned(next);
  }, []);

  return { recents, pinned, addRecent, togglePin, isPinned };
}
